import { createContext, useContext, useMemo } from 'react'
import { Card, Collapse } from '@mui/material'
import { TCFDIVersion, TInvoiceTableTabs } from 'models/invoices.models'
import RazonSocial from 'models/razonSocial'
import { selectInvoicesFilter } from 'lib/helpers/selectors'
import { invoiceTableTabs } from 'lib/utils/Invoices/invoices.utils'
import { useAppSelector } from 'store/hooks'
import SydTabChips from 'components/Shared/SydTabChips/SydTabChips'
import CancelCFDIForm from 'components/Invoices/CFDIs/CancelCFDIForm/CancelCFDIForm'
import CFDIInfo from 'components/Invoices/CFDIs/CFDIInfo/CFDIInfo'
import ChangeCFDIStatusMenu from 'components/Invoices/CFDIs/ChangeCFDIStatusMenu/ChangeCFDIStatusMenu'
import DynamicInvoicesTableColumnsMenu from 'components/Invoices/CFDIs/DynamicCFDIsTableColumnMenu/DynamicCFDIsTableColumnMenu'
import CFDIsAdvancedFilters from 'components/Invoices/CFDIs/CFDIsAdvancedFilters/CFDIsAdvancedFilters'
import CFDIsFilters from 'components/Invoices/CFDIs/CFDIsFilters/CFDIsFilters'
import CFDIRelatedDocs from 'components/Invoices/CFDIs/CFDIRelatedDocs/CFDIRelatedDocs'
import CFDIsTableAdditionalOptions from 'components/Invoices/CFDIs/CFDIsTableAdditionalOptions/CFDIsTableAdditionalOptions'
import CFDIsTable from 'components/Invoices/CFDIs/CFDIsTable/CFDIsTable'
import CFDIsTableDownloadOptions from 'components/Invoices/CFDIs/CFDIsTableDownloadOptions/CFDIsTableDownloadOptions'
import SyncInformationButton from 'components/Invoices/CFDIs/SyncInformationButton/SyncInformationButton'
import useCFDIsContainer, { IUseCFDIsContainer } from './useCFDIsContainer'
import './styles.scss'

export interface ICFDIsContainerProps {
  emisor: RazonSocial | null
  onRedirect: (
    action: 'addPaymentComplement' | 'addCreditNote',
    id: number,
    version?: TCFDIVersion
  ) => void
}

const CFDIsContainerContext = createContext<IUseCFDIsContainer | null>(null)

const CFDIsContainer = ({ emisor, onRedirect }: ICFDIsContainerProps) => {
  const filters = useAppSelector(selectInvoicesFilter)

  const invoicesContainerState = useCFDIsContainer({
    emisor,
    onRedirect,
  })

  const {
    activeTableTab,
    CFDIsPaginatedData,
    isCFDIsTab,
    syncInformationQuery,
    updateState,
  } = invoicesContainerState

  const { onPageChange } = CFDIsPaginatedData

  const { isFetching, isLoading } = syncInformationQuery

  const syncInformationLoading = isFetching || isLoading

  const memoizedContextValue = useMemo(
    () => invoicesContainerState,
    [invoicesContainerState]
  )

  return (
    <CFDIsContainerContext.Provider value={memoizedContextValue}>
      <div className="container-cfdis">
        <div className="container-invoices-filter">
          <CFDIsFilters />
          <Collapse in={filters?.advance}>
            <CFDIsAdvancedFilters />
          </Collapse>
        </div>

        <DynamicInvoicesTableColumnsMenu />

        <article className="invoice-categories-sync-information">
          <div className="invoice-categories-chips">
            <SydTabChips<TInvoiceTableTabs>
              options={invoiceTableTabs}
              selectedValue={activeTableTab}
              handleSelectValue={tabValue => {
                updateState(draft => {
                  draft.activeTableTab = tabValue
                })
                onPageChange(0)
              }}
            />
          </div>

          {isCFDIsTab && (
            <div className="sync-information">
              <SyncInformationButton loading={syncInformationLoading} />
            </div>
          )}
        </article>

        {isCFDIsTab && <CFDIsTableDownloadOptions />}

        <Card
          elevation={1}
          className={`container-invoices-list ${!isCFDIsTab ? 'drafts' : ''}`}>
          <CFDIsTable emisor={emisor} />
        </Card>
        <CFDIsTableAdditionalOptions onRedirect={onRedirect} />
        <ChangeCFDIStatusMenu />
        <CFDIInfo />
        <CFDIRelatedDocs onRedirect={onRedirect} />
        <CancelCFDIForm />
      </div>
    </CFDIsContainerContext.Provider>
  )
}

export const useCFDIsContainerContext = () => {
  const context = useContext(CFDIsContainerContext)
  if (!context) {
    throw new Error(
      'useCFDIsContainerContext must be used within a CFDIsContainerContext'
    )
  }
  return context
}

export default CFDIsContainer
